export interface FoodQuantityUnitConfig {
    maxDecimals: number;
    step: number;
}

const UNIT_CONFIG_BY_NAME: Record<string, FoodQuantityUnitConfig> = {
    cup: { maxDecimals: 2, step: 0.25 },
    grams: { maxDecimals: 0, step: 1 },
    ounces: { maxDecimals: 1, step: 0.1 },
    tbsp: { maxDecimals: 1, step: 0.5 },
    tsp: { maxDecimals: 2, step: 0.5 },
    can: { maxDecimals: 0, step: 1 },
    scoop: { maxDecimals: 0, step: 1 },
    piece: { maxDecimals: 0, step: 1 },
    ltrs: { maxDecimals: 2, step: 0.1 },
    ml: { maxDecimals: 0, step: 1 },
    bag: { maxDecimals: 0, step: 1 }
};

export function getFoodQuantityUnitConfig(unitName: string): FoodQuantityUnitConfig | undefined {
    const key = unitName.trim().toLowerCase();
    return UNIT_CONFIG_BY_NAME[key];
}

export function getMaxDecimalsForUnit(unitName: string): number | undefined {
    return getFoodQuantityUnitConfig(unitName)?.maxDecimals;
}

export function getStepForUnit(unitName: string): number {
    return getFoodQuantityUnitConfig(unitName)?.step ?? 1;
}

export function countDecimals(value: number): number {
    if (Number.isInteger(value)) return 0;
    const s = String(value);
    const i = s.indexOf('.');
    return i === -1 ? 0 : s.length - i - 1;
}

export function isValidDecimalPlaces(value: number, maxDecimals: number): boolean {
    return countDecimals(value) <= maxDecimals;
}

/** Returns a fraction label for quarter-equivalent values (Cup only), or null if not applicable. */
export function getCupQuarterFractionTooltip(value: number): string | null {
    if (value < 0 || !Number.isFinite(value)) return null;
    const quarters = Math.round(value * 4);
    const remainder = quarters % 4;
    if (remainder === 0) {
        const whole = quarters / 4;
        return whole === 0 ? '0' : String(whole);
    }
    const whole = Math.floor(quarters / 4);
    const frac = remainder === 1 ? '1/4' : remainder === 2 ? '1/2' : '3/4';
    return whole === 0 ? frac : `${whole} ${frac}`;
}

/** True if the value is a quarter-equivalent (multiple of 0.25). */
export function isQuarterEquivalent(value: number): boolean {
    if (!Number.isFinite(value)) return false;
    return Math.abs(value * 4 - Math.round(value * 4)) < 1e-6;
}
